import { Spinner } from '@chakra-ui/react'
import { Card } from '@nextui-org/react'
import { useQuery } from '@tanstack/react-query'
import { FC } from 'react'
import {
  getChartByProperty,
  type BaseChartData,
  type ChartProperty,
} from '@/api/chart/chart.service'
import useChartStore from '../../store/chart/useChartStore'
import '../../styles/responsive.css'
import { PropertyName } from '../../types/apiResponse/chartTypes'
import { makeCSVFormV2 } from '../../utils/chart/makeCSV'
import { makeChartYAxisTitle } from '../../utils/chart/stringUtils'
import { DownloadCSV } from './DownloadCSV'
import { EmptyChartState } from './EmptyChartState'
import { SingleDomainPlot } from './plot/SingleDomainPlot'

export type ChartComponentProps = {
  property: PropertyName
  schoolColors: { [id: string]: string }
}

/**
 * Map PropertyName to ChartProperty for API calls
 * Note: 'nh3no2' uses separate WaterQualityChartComponent
 */
function toChartProperty(property: PropertyName): ChartProperty | null {
  const mapping: Record<string, ChartProperty> = {
    averageWeight: 'averageWeight',
    sampleWeight: 'sampleWeight',
    feedingAmount: 'feedingAmount',
    count: 'count',
    biomass: 'biomass',
  }
  return mapping[property] ?? null
}

export const ChartComponent: FC<ChartComponentProps> = ({ property, schoolColors }) => {
  const { selectedSchools, selectedTanks } = useChartStore()
  const chartProperty = toChartProperty(property)
  const labels = Object.fromEntries(selectedTanks.map((t) => [t.schoolId, t.tankName]))

  const chartQueryResult = useQuery({
    queryKey: ['chart', property, selectedSchools],
    queryFn: async () => {
      if (selectedSchools.length === 0) return null
      if (!chartProperty) return null // nh3no2 not supported
      return await getChartByProperty(chartProperty, selectedSchools)
    },
    staleTime: 3600,
    refetchOnMount: false,
  })

  const csvData =
    chartQueryResult.data && selectedTanks.length !== 0
      ? makeCSVFormV2(chartQueryResult.data, property, selectedTanks)
      : null

  return (
    <div className="flex flex-col">
      <div className="mx-1 mb-1 flex flex-row justify-between">
        <p className="font-bold text-[1.25rem]">{makeChartYAxisTitle(property)}</p>
        <DownloadCSV
          csvData={csvData ? csvData : []}
          isActive={
            !!csvData &&
            !!chartQueryResult.data &&
            Object.keys(chartQueryResult.data.xyPointsPerSchoolId).length > 0
          }
        />
      </div>
      {chartQueryResult.isLoading ? (
        <Card
          shadow="none"
          className="h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] min-w-[100%] border-[1px] border-tdpldarkgrey border-opacity-60
          flex items-center justify-center"
        >
          <Spinner color="#B98CA4" emptyColor="gray.200" size="lg" />
        </Card>
      ) : chartQueryResult.isError ? (
        <EmptyChartState
          title={makeChartYAxisTitle(property)}
          message="Failed to load chart data. Please try again."
        />
      ) : !chartQueryResult.data || chartQueryResult.data === null ? (
        <EmptyChartState
          title={makeChartYAxisTitle(property)}
          message="No data available for the selected criteria"
        />
      ) : (
        <Card
          shadow="none"
          className="min-w-[100%] border-[1px] border-tdpldarkgrey border-opacity-60"
        >
          <div className="p-0 h-[300px] sm:h-[400px] md:h-[450px] lg:h-[500px] overflow-hidden">
            {Object.keys(chartQueryResult.data.xyPointsPerSchoolId).length > 0 ? (
              <SingleDomainPlot
                schoolColors={schoolColors}
                property={property}
                chartResponse={chartQueryResult.data as BaseChartData}
                timeUnit="day"
                labels={labels}
              />
            ) : (
              <EmptyChartState
                title={makeChartYAxisTitle(property)}
                message="No data available for this property"
              />
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
